import { type FieldPolicy, InMemoryCache } from "@apollo/client";
import { relayStylePagination } from "@apollo/client/utilities";
import {
	type ApplicationProgressValue,
	initialApplicationProgress,
	readVisibleApplicationProgress,
} from "../applicationProgressVar";
import { lastActiveInvestingEntityVar } from "../lastActiveInvestingEntityVar";
import introspection from "../introspection-result";

function cursorStackPagination(keyArgs: string[]): FieldPolicy<unknown> {
	return {
		keyArgs,
		merge(_existing, incoming) {
			return incoming;
		},
	};
}

/**
 * Stamps the client-schema `__typename`s onto application progress.
 *
 * Apollo Client 4 resolves an `@client` field through `LocalState`, which reads
 * the field's children off the object the type policy returns and needs a
 * `__typename` to do it. Without one every query selecting
 * `Offer.applicationProgress` failed with "Could not resolve __typename", and
 * the whole application flow rendered its error state. The progress itself is
 * stored without typenames (localStorage), so they are added on the way out.
 */
function withApplicationProgressTypenames(
	progress: Partial<ApplicationProgressValue>,
) {
	return {
		...progress,
		__typename: "ApplicationProgress" as const,
		unitCountDecimal: progress.unitCountDecimal
			? {
					...progress.unitCountDecimal,
					__typename: "FixedPointNumber" as const,
				}
			: progress.unitCountDecimal,
		investmentAmountHPM: progress.investmentAmountHPM
			? {
					...progress.investmentAmountHPM,
					__typename: "ApplicationProgressInvestmentAmount" as const,
				}
			: progress.investmentAmountHPM,
		customFieldSections: progress.customFieldSections?.map((section) => ({
			...section,
			__typename: "ApplicationProgressCustomFieldSection" as const,
			values: section.values?.map((value) => ({
				...value,
				__typename: "ApplicationProgressCustomFieldSectionValue" as const,
			})),
		})),
	};
}

/**
 * @param CacheImpl - the `InMemoryCache` to build. The Next.js integration ships
 *   its own subclass, which carries the state it transports from the server, so
 *   the browser client is given that one rather than Apollo's.
 */
export const createInMemoryCache = <CacheClass extends typeof InMemoryCache>(
	CacheImpl: CacheClass = InMemoryCache as CacheClass,
): InstanceType<CacheClass> =>
	new CacheImpl({
		possibleTypes: introspection.possibleTypes,
		typePolicies: {
			// One entity for the signed-in account. Application-progress scoping
			// (APP-26441) reads its `id`, so a query selecting some *other*
			// account here would silently re-scope an investor's saved progress.
			Account: {
				keyFields: [],
				fields: {
					// Cross-entity documents view paginates with the cursor-stack
					// (prev/next) paginator, which replaces the visible rows per page.
					// It must NOT merge edges across pages like relayStylePagination().
					searchDocuments: cursorStackPagination([
						"filters",
						"keywords",
						"publishDateFrom",
						"publishDateTo",
						"sort",
						"first",
						"after",
					]),
					lastActiveInvestingEntityId: {
						read: (_, { readField }) => {
							const id = readField<string>("id");
							const map = lastActiveInvestingEntityVar();

							if (!id) {
								return null;
							}

							return map[id] ?? null;
						},
					},
				},
			},
			CustomFieldResponse: {
				// Don't normalize CustomFieldResponse objects to prevent cache conflicts
				// Each response is unique to its parent custom field
				keyFields: false,
			},
			IndividualInvestingEntity: {
				keyFields: false,
			},
			JointIndividualInvestingEntity: {
				fields: {
					individualOne: {
						merge: false,
					},
					individualTwo: {
						merge: false,
					},
				},
			},
			AssociatedAccount: {
				keyFields: ["id"],
			},
			Holding: {
				keyFields: ["id"],
				fields: {
					searchTransactions: relayStylePagination(),
				},
			},
			InvestingEntity: {
				keyFields: ["id"],
				fields: {
					searchDocuments: relayStylePagination(),
					searchTransactions: cursorStackPagination([
						"filters",
						"fundIds",
						"unitClassIds",
						"transactionTypes",
						"tagsFilter",
						"dateFrom",
						"dateTo",
						"sort",
						"first",
						"after",
					]),
				},
			},
			Session: {
				keyFields: [],
			},
			OfferInvestor: {
				keyFields: false,
			},
			Offer: {
				fields: {
					applicationProgress: {
						read(_, { readField }) {
							const slug = readField<string>("slug");
							// The empty default names every field, as null: LocalState
							// leaves out a field whose value a selected child is missing
							// from, where Apollo Client 3's cache read tolerated it.
							return withApplicationProgressTypenames(
								(slug ? readVisibleApplicationProgress(slug) : undefined) ?? {
									...initialApplicationProgress(false),
									completedSteps: [],
									activeStep: null,
								},
							);
						},
					},
				},
			},
			Portfolio: {
				keyFields: (portfolio) =>
					portfolio.investingEntityId ? ["investingEntityId"] : [],
				fields: {
					holdingById: {
						read: (_, { args, toReference }) =>
							toReference({
								__typename: "Holding",
								id: args?.id,
							}),
					},
				},
			},
			Query: {
				fields: {
					searchDocuments: relayStylePagination(),
					searchTransactions: relayStylePagination(),
					searchDistributionTransactions: cursorStackPagination([
						"activeInvestingEntityId",
						"after",
						"first",
						"keywords",
						"fundIds",
						"unitClassIds",
						"dateFrom",
						"dateTo",
						"sort",
						"distributionTransactionStatusFilter",
					]),
				},
			},
			Fund: {
				keyFields: false,
			},
		},
	}) as InstanceType<CacheClass>;
